import { Link } from '@remix-run/react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import MODEL_TEMPLATES from '~/lib/chats/models/model-templates';
import MODEL_TEMPLATES_CHARACTERS from '~/lib/chats/models/model-templates-characters';
import ModelTemplate from '~/lib/chats/types/model-template';
import TypingIndicator from '../chats/TypingIndicator';

const MY_TEMPLATES = MODEL_TEMPLATES_CHARACTERS.concat(MODEL_TEMPLATES);

const ChatBot = lazy(() => import('~/components/chatbot'));

const CHAT_PARTS = [
  'you with unlocking the mysteries of the Bible.',
  'with exploring the depths of faith like never before.',
  'with answering your burning questions about Faith.',
  'navigate the complex world of biblical teachings.',
  'with unraveling the secrets of faith and biblical truth.',
  'guide you on a journey through the Bible.',
  'provide insights into the most challenging biblical concepts.',
  'you discover new perspectives on faith.',
  'with taking an adventure through biblical teachings.',
  'you dive deep into the ocean of biblical wisdom.',
];

const ChatBotWidget = ({
  viewMode = 'icon',
  visible = true,
  cardClassName = '',
  imgClassName = '',
  containerClassName = '',
  chatWithTextClassName = '',
  chatWithText2ClassName = '',
  insideNavigation = false,
}: {
  viewMode?: 'icon' | 'card';
  visible?: boolean;
  cardClassName?: string;
  imgClassName?: string;
  containerClassName?: string;
  chatWithTextClassName?: string;
  chatWithText2ClassName?: string;
  insideNavigation?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(''); // Add this line
  const [currentCharacter, setCurrentCharacter] =
    useState<ModelTemplate | null>(null);

  const isOnBibleCharacterPage =
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/bible-characters/');
  const isOnHomePage =
    typeof window !== 'undefined' && window.location.pathname === '/';

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * MY_TEMPLATES.length);
    setCurrentCharacter(MY_TEMPLATES[randomIndex]);
  }, []);

  const toggleWidget = () => {
    setIsOpen(!isOpen);
  };

  const cardContent = () => {
    const assistantType =
      currentCharacter?.type === 'character' ? 'bible character' : 'assistant';
    // const chatWithText = `${randomizeIntro(currentCharacter?.name || '')}`;
    const chatWithText = '';

    const chatWithText2 = useMemo(() => {
      const randomPart =
        CHAT_PARTS[Math.floor(Math.random() * CHAT_PARTS.length)];
      return `${currentCharacter?.name} is an AI powered ${assistantType} who can help ${randomPart}`;
    }, [currentCharacter?.name, assistantType]);

    const handleNavigate = (e?: React.MouseEvent) => {
      e?.preventDefault();
      if (!currentCharacter?.slug || !inputValue) return;

      const url = `/bible-characters/${currentCharacter.slug}`;
      const query = inputValue
        ? `message=${encodeURIComponent(inputValue)}`
        : '';

      // Use window location for navigation to avoid page refresh
      window.location.href = `${url}${query ? `?${query}` : ''}`;
    };

    const centerMe = ' relative max-w-md left-1/2 transform -translate-x-1/2';

    return (
      <>
        <div
          className={`p-6 rounded-2xl bg-white dark:bg-gray-800 border-2 border-black dark:border-white ${cardClassName}`}
        >
          <img
            src={currentCharacter?.image}
            alt={`${currentCharacter?.name} avatar`}
            className={`w-16 h-16 rounded-full mx-auto border-2 border-black dark:border-white ${imgClassName}`}
          />

          <h3
            className={`mt-4 mb-2 text-center text-xl font-bold text-black dark:text-white ${chatWithTextClassName}`}
          >
            {chatWithText}
          </h3>

          <small>
            <p
              className={`hidden md:block text-center text-sm mt-1 px-4 normal-case text-gray-800 dark:text-gray-200 font-medium ${chatWithText2ClassName} ${centerMe}`}
            >
              {chatWithText2}
            </p>
          </small>

          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Enter' && handleNavigate()}
            placeholder="Ask a question..."
            className={
              'w-full px-4 py-3 mt-6 -mb-1 rounded-lg border-2 border-black dark:border-white bg-transparent focus:ring-2 focus:ring-transparent  transition-all duration-200 placeholder-gray-600 dark:placeholder-gray-400' +
              centerMe
            }
          />

          <button
            onClick={handleNavigate}
            className={
              'mt-4 w-full flex items-center justify-center px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-[4rem] transition-colors duration-200 font-medium space-x-2 border-2 border-black dark:border-white hover:bg-white hover:text-black dark:hover:bg-gray-800 dark:hover:text-white' +
              centerMe
            }
          >
            <MessageCircle className="w-5 h-5" />
            <span>Chat Now</span>
          </button>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (isOnHomePage && !insideNavigation) {
      setCurrentCharacter(
        MY_TEMPLATES.find(
          (t: ModelTemplate) => t.name === 'A.I. Bible Assistant',
        ) || null,
      );
    }
  }, [isOnHomePage]);

  if (isOnBibleCharacterPage || !visible) {
    return null;
  }

  return (
    <div
      className={
        viewMode === 'card'
          ? 'relative ' + containerClassName
          : 'fixed bottom-4 right-4 z-50 ' + containerClassName
      }
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        transition={{ duration: 0.3 }}
        className={'relative '}
      >
        {viewMode === 'card' ? (
          cardContent()
        ) : (
          <>
            {isOpen && (
              <>
                <div
                  className="fixed inset-0 bg-black bg-opacity-70"
                  onClick={toggleWidget}
                />
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="fixed bottom-20 right-4 bg-white dark:bg-dark-900 rounded-lg p-4 pb-0"
                    style={{
                      width: '400px',
                      maxWidth: 'calc(100% - 2rem)',
                      height: 'auto',
                      maxHeight: '80vh',
                      overflow: 'auto',
                      border: '1px solid #1b212e',
                    }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h2
                        className="text-sm font-bold"
                        style={{
                          borderRadius: '1.5rem',
                          padding: '0.15rem 1rem 0.15rem 0.15rem',
                          whiteSpace: 'nowrap',
                          flexWrap: 'wrap',
                          width: 'auto',
                          display: 'flex',
                          gap: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          src={currentCharacter?.image}
                          alt={currentCharacter?.name + ' avatar'}
                          className="w-6 h-6 mr-1 rounded-full border-2 border-solid !h-8 !w-8 min-w-[2rem] border-[#808fa9]"
                        />
                        {currentCharacter?.name}
                      </h2>
                      <Link
                        to={`/bible-characters/${currentCharacter?.slug}`}
                        target="_blank"
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <a target="_blank" rel="noopener noreferrer">
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      </Link>
                    </div>
                    <Suspense
                      fallback={
                        <div className="w-full flex justify-center mt-6 mb-4">
                          <TypingIndicator />
                        </div>
                      }
                    >
                      <ChatBot template={currentCharacter} widget />
                    </Suspense>
                  </motion.div>
                </AnimatePresence>
              </>
            )}
            {isOpen ? (
              <motion.button
                initial={{ scale: 1 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={toggleWidget}
                className="fixed bottom-4 right-4 bg-primary-500 text-white p-3 shadow-lg rounded-full w-14 h-14"
              >
                <svg
                  className="w-6 h-6 m-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ transform: 'scale(1.25) translateY(1px)' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </motion.button>
            ) : (
              <motion.button
                initial={{ scale: 1 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={toggleWidget}
                className="bg-primary-500 text-white p-3 shadow-lg rounded-full w-14 h-14"
              >
                <span className="relative flex justify-center">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transform: 'scale(1.25) translateY(1px) rotate(-5deg)',
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                  <span
                    className="absolute -top-[3px] -right-[2px] w-2 h-2 bg-red-500 rounded-full"
                    style={{
                      animation: 'scaleAnimation 1s infinite',
                      animationDelay: '0.5s',
                    }}
                  />
                </span>
              </motion.button>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ChatBotWidget;
